import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import CardNav from './CardNav';
import ResourceSection from './ResourceSection';
import NeonCountdown from './NeonCountdown';
import '../App.css';

// Helper function to get avatar URL for a designer
const getAvatarUrl = (designer) => {
  // If designer is an object with avatarUrl, use it
  if (typeof designer === 'object' && designer.avatarUrl) {
    return designer.avatarUrl;
  }
  // Return null if no avatar (will show initials instead)
  return null;
};

// Helper function to get designer initials
const getDesignerInitials = (designer) => {
  const name = typeof designer === 'string' ? designer : designer.name;
  if (!name) return '??';
  const initials = name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
  return initials || '??';
};

function GameManagement({ game, onBack, onStartGame, onNavigateToActiveGame, onNavigateToReview }) {
  const [gameDetails, setGameDetails] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [clientChanges, setClientChanges] = useState({});
  const [bonusRequirements, setBonusRequirements] = useState({});
  const [designers, setDesigners] = useState([]);
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gameStatus, setGameStatus] = useState(game?.game_status || 'draft'); // draft, published, started, finished
  const [joinCode, setJoinCode] = useState(null);
  const [participantUserIds, setParticipantUserIds] = useState(new Set()); // Track which user IDs have joined
  
  // Editing state - only enabled for draft games
  const [editingSection, setEditingSection] = useState(null); // { sessionKey, section: 'brief' | 'audience' | 'timer' | 'requirements' | 'clientChanges' }
  const [editingSessionName, setEditingSessionName] = useState(null); // sessionKey
  const [editingRequirement, setEditingRequirement] = useState(null); // { sessionKey, index } - for individual requirement editing
  const [editingClientChange, setEditingClientChange] = useState(null); // { sessionKey, index } - for individual client change editing
  const [editingClientChangeValues, setEditingClientChangeValues] = useState(null); // { text, points, timing } - temporary values while editing
  const [editingBonus, setEditingBonus] = useState(null); // sessionKey
  const [editingBonusValues, setEditingBonusValues] = useState(null); // { text, points } - temporary values while editing
  const [newRequirementText, setNewRequirementText] = useState('');
  const [newClientChange, setNewClientChange] = useState({ text: '', points: 1 });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (game) {
      console.log('GameManagement: Game prop changed, loading data for game:', game.id, 'current status:', game.game_status);
      // Always reload game data to ensure we have the latest status from database
      // Add a small delay to ensure any pending database updates are saved
      const timer = setTimeout(() => {
        loadGameData();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [game?.id]); // Reload when game ID changes - loadGameData will fetch fresh status from DB

  // Auto-navigate to ActiveGameView if game is already published
  useEffect(() => {
    if (gameStatus === 'published' && onNavigateToActiveGame && game?.id) {
      console.log('[AUTO-NAV] Game is published, auto-navigating to ActiveGameView');
      // Small delay to ensure component has fully rendered
      const navTimer = setTimeout(() => {
        onNavigateToActiveGame();
      }, 100);
      return () => clearTimeout(navTimer);
    }
  }, [gameStatus, game?.id, onNavigateToActiveGame]);

  // Real-time subscription for game_participants to update join status
  useEffect(() => {
    if (!game?.id) return;

    console.log('🔔 [GameManagement] Setting up real-time participant subscription for game:', game.id);
    
    const participantChannel = supabase
      .channel(`game-management-participants-${game.id}`)
      .on('postgres_changes',
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'game_participants',
          filter: `game_id=eq.${game.id}`
        },
        async (payload) => {
          console.log('[GameManagement] [REALTIME] New participant joined:', payload.new);
          const newUserId = payload.new.user_id;
          
          // Update participant user IDs set
          setParticipantUserIds(prev => {
            const updated = new Set(prev);
            updated.add(newUserId);
            console.log('[GameManagement] [REALTIME] Updated participantUserIds:', Array.from(updated));
            return updated;
          });
          
          // Reload participants to refresh designer list with new participant info
          try {
            const { data: participants, error: participantsError } = await supabase
              .from('game_participants')
              .select('user_id')
              .eq('game_id', game.id);

            if (!participantsError && participants && participants.length > 0) {
              setParticipantUserIds(new Set(participants.map(p => p.user_id)));
              
              // Reload game data to refresh designer list with participant info
              const { data: userProfiles } = await supabase
                .from('user_profiles')
                .select('id, full_name, email')
                .in('id', participants.map(p => p.user_id));

              if (userProfiles && userProfiles.length > 0) {
                setDesigners(prevDesigners => {
                  const profileMap = new Map(userProfiles.map(profile => [profile.id, profile]));
                  // Track which user IDs have been matched to existing designers
                  const matchedUserIds = new Set();
                  
                  // First pass: Update existing designers
                  const updatedDesigners = prevDesigners.map(designer => {
                    const designerName = typeof designer === 'string' ? designer : designer.name;
                    
                    // Try to find matching profile
                    const matchingProfile = Array.from(profileMap.values()).find(profile => {
                      // Skip if this profile is already matched
                      if (matchedUserIds.has(profile.id)) return false;
                      
                      const profileNameLower = profile.full_name?.toLowerCase().trim();
                      const profileEmailLower = profile.email?.toLowerCase().trim();
                      const designerNameLower = designerName?.toLowerCase().trim();
                      const designerEmailLower = typeof designer === 'object' ? designer.email?.toLowerCase().trim() : null;
                      
                      // Check for existing match by ID
                      if (typeof designer === 'object' && designer.userId === profile.id) {
                        return true;
                      }
                      
                      // Check by name
                      if (profileNameLower === designerNameLower) return true;
                      
                      // Check by email
                      if (designerEmailLower && profileEmailLower && designerEmailLower === profileEmailLower) return true;
                      
                      // Partial matches
                      if (designerNameLower && profileNameLower) {
                        const designerParts = designerNameLower.split(' ');
                        const profileParts = profileNameLower.split(' ');
                        if (designerParts[0] && profileParts[0] && designerParts[0] === profileParts[0]) return true;
                      }
                      
                      return false;
                    });
                    
                    if (matchingProfile) {
                      matchedUserIds.add(matchingProfile.id);
                      const fullParticipantName = matchingProfile.full_name || matchingProfile.email || 'Unknown';
                      return {
                        ...(typeof designer === 'string' ? { name: designer } : designer),
                        userId: matchingProfile.id,
                        name: typeof designer === 'string' ? fullParticipantName : (designer.name || fullParticipantName),
                        email: matchingProfile.email || (typeof designer === 'object' ? designer.email : null)
                      };
                    }
                    return designer;
                  });

                  // Second pass: Add any participants that weren't matched to existing designers
                  userProfiles.forEach(profile => {
                    if (!matchedUserIds.has(profile.id)) {
                      console.log('[GameManagement] [REALTIME] Adding new designer from participant:', profile);
                      const fullParticipantName = profile.full_name || profile.email || 'Unknown';
                      const nameParts = fullParticipantName.split(' ');
                      updatedDesigners.push({
                        name: fullParticipantName,
                        firstName: nameParts[0] || '',
                        lastName: nameParts.slice(1).join(' ') || '',
                        email: profile.email || null,
                        avatarUrl: null,
                        userId: profile.id,
                        isParticipant: true
                      });
                    }
                  });

                  return updatedDesigners;
                });
              }
            }
          } catch (error) {
            console.error('[GameManagement] [REALTIME] Error reloading participants:', error);
          }
        }
      )
      .on('postgres_changes',
        { 
          event: 'DELETE', 
          schema: 'public', 
          table: 'game_participants',
          filter: `game_id=eq.${game.id}`
        },
        async (payload) => {
          console.log('[GameManagement] [REALTIME] Participant left:', payload.old);
          const removedUserId = payload.old.user_id;
          
          // Update participant user IDs set
          setParticipantUserIds(prev => {
            const updated = new Set(prev);
            updated.delete(removedUserId);
            console.log('[GameManagement] [REALTIME] Updated participantUserIds after removal:', Array.from(updated));
            return updated;
          });
          
          // Reload participants
          try {
            const { data: participants } = await supabase
              .from('game_participants')
              .select('user_id')
              .eq('game_id', game.id);

            if (participants) {
              setParticipantUserIds(new Set(participants.map(p => p.user_id)));
            }
          } catch (error) {
            console.error('[GameManagement] [REALTIME] Error reloading participants:', error);
          }
        }
      )
      .subscribe();

    return () => {
      console.log('🔕 [GameManagement] Unsubscribing from participant channel');
      participantChannel.unsubscribe();
    };
  }, [game?.id]);

  // Polling fallback to check for new participants (in case real-time fails)
  useEffect(() => {
    if (!game?.id) return;

    const pollInterval = setInterval(async () => {
      try {
        const { data: participants, error } = await supabase
          .from('game_participants')
          .select('user_id')
          .eq('game_id', game.id);

        if (!error && participants) {
          const currentParticipantIds = new Set(participants.map(p => p.user_id));
          
          // Check if there are new participants by comparing with current state
          setParticipantUserIds(prev => {
            const hasNewParticipants = Array.from(currentParticipantIds).some(id => !prev.has(id));
            
            if (hasNewParticipants) {
              console.log('[GameManagement] [POLLING] New participants detected, reloading participants...');
              // Reload participants and update designers
              (async () => {
                try {
                  const userIds = participants.map(p => p.user_id);
                  const { data: userProfiles } = await supabase
                    .from('user_profiles')
                    .select('id, full_name, email')
                    .in('id', userIds);

                  if (userProfiles && userProfiles.length > 0) {
                    setDesigners(prevDesigners => {
                      const profileMap = new Map(userProfiles.map(profile => [profile.id, profile]));
                      const matchedUserIds = new Set();
                      
                      // Update existing designers
                      const updatedDesigners = prevDesigners.map(designer => {
                        const designerName = typeof designer === 'string' ? designer : designer.name;
                        const matchingProfile = Array.from(profileMap.values()).find(profile => {
                          if (matchedUserIds.has(profile.id)) return false;
                          const profileNameLower = profile.full_name?.toLowerCase().trim();
                          const designerNameLower = designerName?.toLowerCase().trim();
                          if (typeof designer === 'object' && designer.userId === profile.id) return true;
                          if (profileNameLower === designerNameLower) return true;
                          if (designerNameLower && profileNameLower) {
                            const designerParts = designerNameLower.split(' ');
                            const profileParts = profileNameLower.split(' ');
                            if (designerParts[0] && profileParts[0] && designerParts[0] === profileParts[0]) return true;
                          }
                          return false;
                        });
                        
                        if (matchingProfile) {
                          matchedUserIds.add(matchingProfile.id);
                          const fullParticipantName = matchingProfile.full_name || matchingProfile.email || 'Unknown';
                          return {
                            ...(typeof designer === 'string' ? { name: designer } : designer),
                            userId: matchingProfile.id,
                            name: typeof designer === 'string' ? fullParticipantName : (designer.name || fullParticipantName),
                            email: matchingProfile.email || (typeof designer === 'object' ? designer.email : null)
                          };
                        }
                        return designer;
                      });

                      // Add new participants that weren't matched
                      userProfiles.forEach(profile => {
                        if (!matchedUserIds.has(profile.id)) {
                          const fullParticipantName = profile.full_name || profile.email || 'Unknown';
                          const nameParts = fullParticipantName.split(' ');
                          updatedDesigners.push({
                            name: fullParticipantName,
                            firstName: nameParts[0] || '',
                            lastName: nameParts.slice(1).join(' ') || '',
                            email: profile.email || null,
                            avatarUrl: null,
                            userId: profile.id,
                            isParticipant: true
                          });
                        }
                      });

                      return updatedDesigners;
                    });
                  }
                } catch (err) {
                  console.error('[GameManagement] [POLLING] Error reloading participants:', err);
                }
              })();
              return currentParticipantIds;
            }
            return prev;
          });
        }
      } catch (error) {
        console.error('[GameManagement] [POLLING] Error checking participants:', error);
      }
    }, 5000); // Poll every 5 seconds as fallback

    return () => clearInterval(pollInterval);
  }, [game?.id]);

  // Real-time subscription for game_participants to update join status
  useEffect(() => {
    if (!game?.id) return;

    console.log('🔔 [GameManagement] Setting up real-time participant subscription for game:', game.id);
    
    const participantChannel = supabase
      .channel(`game-management-participants-${game.id}`)
      .on('postgres_changes',
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'game_participants',
          filter: `game_id=eq.${game.id}`
        },
        async (payload) => {
          console.log('[GameManagement] [REALTIME] New participant joined:', payload.new);
          const newUserId = payload.new.user_id;
          
          // Update participant user IDs set
          setParticipantUserIds(prev => {
            const updated = new Set(prev);
            updated.add(newUserId);
            console.log('[GameManagement] [REALTIME] Updated participantUserIds:', Array.from(updated));
            return updated;
          });
          
          // Reload participants to refresh designer list with new participant info
          try {
            const { data: participants, error: participantsError } = await supabase
              .from('game_participants')
              .select('user_id')
              .eq('game_id', game.id);

            if (!participantsError && participants && participants.length > 0) {
              setParticipantUserIds(new Set(participants.map(p => p.user_id)));
              
              // Reload game data to refresh designer list with participant info
              const { data: userProfiles } = await supabase
                .from('user_profiles')
                .select('id, full_name, email')
                .in('id', participants.map(p => p.user_id));

              if (userProfiles && userProfiles.length > 0) {
                setDesigners(prevDesigners => {
                  const profileMap = new Map(userProfiles.map(profile => [profile.id, profile]));
                  // Track which user IDs have been matched to existing designers
                  const matchedUserIds = new Set();
                  
                  // First pass: Update existing designers
                  const updatedDesigners = prevDesigners.map(designer => {
                    const designerName = typeof designer === 'string' ? designer : designer.name;
                    
                    // Try to find matching profile
                    const matchingProfile = Array.from(profileMap.values()).find(profile => {
                      // Skip if this profile is already matched
                      if (matchedUserIds.has(profile.id)) return false;
                      
                      const profileNameLower = profile.full_name?.toLowerCase().trim();
                      const profileEmailLower = profile.email?.toLowerCase().trim();
                      const designerNameLower = designerName?.toLowerCase().trim();
                      const designerEmailLower = typeof designer === 'object' ? designer.email?.toLowerCase().trim() : null;
                      
                      // Check for existing match by ID
                      if (typeof designer === 'object' && designer.userId === profile.id) {
                        return true;
                      }
                      
                      // Check by name
                      if (profileNameLower === designerNameLower) return true;
                      
                      // Check by email
                      if (designerEmailLower && profileEmailLower && designerEmailLower === profileEmailLower) return true;
                      
                      // Partial matches
                      if (designerNameLower && profileNameLower) {
                        const designerParts = designerNameLower.split(' ');
                        const profileParts = profileNameLower.split(' ');
                        if (designerParts[0] && profileParts[0] && designerParts[0] === profileParts[0]) return true;
                      }
                      
                      return false;
                    });
                    
                    if (matchingProfile) {
                      matchedUserIds.add(matchingProfile.id);
                      const fullParticipantName = matchingProfile.full_name || matchingProfile.email || 'Unknown';
                      return {
                        ...(typeof designer === 'string' ? { name: designer } : designer),
                        userId: matchingProfile.id,
                        name: typeof designer === 'string' ? fullParticipantName : (designer.name || fullParticipantName),
                        email: matchingProfile.email || (typeof designer === 'object' ? designer.email : null)
                      };
                    }
                    return designer;
                  });

                  // Second pass: Add any participants that weren't matched to existing designers
                  userProfiles.forEach(profile => {
                    if (!matchedUserIds.has(profile.id)) {
                      console.log('[GameManagement] [REALTIME] Adding new designer from participant:', profile);
                      const fullParticipantName = profile.full_name || profile.email || 'Unknown';
                      const nameParts = fullParticipantName.split(' ');
                      updatedDesigners.push({
                        name: fullParticipantName,
                        firstName: nameParts[0] || '',
                        lastName: nameParts.slice(1).join(' ') || '',
                        email: profile.email || null,
                        avatarUrl: null,
                        userId: profile.id,
                        isParticipant: true
                      });
                    }
                  });

                  return updatedDesigners;
                });
              }
            }
          } catch (error) {
            console.error('[GameManagement] [REALTIME] Error reloading participants:', error);
          }
        }
      )
      .on('postgres_changes',
        { 
          event: 'DELETE', 
          schema: 'public', 
          table: 'game_participants',
          filter: `game_id=eq.${game.id}`
        },
        async (payload) => {
          console.log('[GameManagement] [REALTIME] Participant left:', payload.old);
          const removedUserId = payload.old.user_id;
          
          // Update participant user IDs set
          setParticipantUserIds(prev => {
            const updated = new Set(prev);
            updated.delete(removedUserId);
            console.log('[GameManagement] [REALTIME] Updated participantUserIds after removal:', Array.from(updated));
            return updated;
          });
          
          // Reload participants
          try {
            const { data: participants } = await supabase
              .from('game_participants')
              .select('user_id')
              .eq('game_id', game.id);

            if (participants) {
              setParticipantUserIds(new Set(participants.map(p => p.user_id)));
            }
          } catch (error) {
            console.error('[GameManagement] [REALTIME] Error reloading participants:', error);
          }
        }
      )
      .subscribe();

    return () => {
      console.log('🔕 [GameManagement] Unsubscribing from participant channel');
      participantChannel.unsubscribe();
    };
  }, [game?.id]);

  // Polling fallback to check for new participants (in case real-time fails)
  useEffect(() => {
    if (!game?.id) return;

    const pollInterval = setInterval(async () => {
      try {
        const { data: participants, error } = await supabase
          .from('game_participants')
          .select('user_id')
          .eq('game_id', game.id);

        if (!error && participants) {
          const currentParticipantIds = new Set(participants.map(p => p.user_id));
          const previousParticipantIds = participantUserIds;
          
          // Check if there are new participants
          const hasNewParticipants = Array.from(currentParticipantIds).some(id => !previousParticipantIds.has(id));
          
          if (hasNewParticipants) {
            console.log('[GameManagement] [POLLING] New participants detected, reloading...');
            setParticipantUserIds(currentParticipantIds);
            // Reload game data to refresh designer list
            loadGameData();
          }
        }
      } catch (error) {
        console.error('[GameManagement] [POLLING] Error checking participants:', error);
      }
    }, 5000); // Poll every 5 seconds as fallback

    return () => clearInterval(pollInterval);
  }, [game?.id, participantUserIds]);

  const loadResources = async () => {
    if (!game?.id) {
      setResources([]);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('resources')
        .select('*')
        .eq('game_id', game.id)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error loading resources:', error);
        setResources([]);
      } else if (data) {
        // Process resources to get proper image URLs
        const resourcesWithUrls = await Promise.all(data.map(async (res) => {
          let externalUrl = res.url;
          let imageUrl = null;
          
          if (res.type === 'link') {
            // For link resources: url = external link, file_path = image path
            if (res.file_path) {
              const { data: { publicUrl } } = supabase.storage
                .from('design-submissions')
                .getPublicUrl(res.file_path);
              imageUrl = publicUrl;
            }
          } else {
            // For file resources: url = image URL
            if (res.file_path) {
              const { data: { publicUrl } } = supabase.storage
                .from('design-submissions')
                .getPublicUrl(res.file_path);
              imageUrl = publicUrl;
            }
          }
          
          return {
            ...res,
            imageUrl: imageUrl || res.url,
            externalUrl: res.type === 'link' ? externalUrl : null
          };
        }));
        
        setResources(resourcesWithUrls);
      }
    } catch (error) {
      console.error('Error loading resources:', error);
      setResources([]);
    }
  };

  const loadGameData = async (retryCount = 0) => {
    try {
      setLoading(true);
      
      console.log(`Loading game data for game ID: ${game.id} (attempt ${retryCount + 1})`);
      
      // Load session briefs
      const { data: briefs, error: briefsError } = await supabase
        .from('session_briefs')
        .select('*')
        .eq('game_id', game.id);

      if (briefsError) {
        console.error('Error loading briefs:', briefsError);
      } else {
        console.log('Loaded briefs:', briefs);
        // If no briefs found and this is first attempt, retry after a delay
        if (!briefs || briefs.length === 0 && retryCount === 0) {
          console.log('No briefs found on first attempt, retrying in 1 second...');
          setTimeout(() => {
            loadGameData(1);
          }, 1000);
          return;
        }
      }

      // Load session requirements
      const { data: requirements, error: requirementsError } = await supabase
        .from('session_requirements')
        .select('*')
        .eq('game_id', game.id);

      if (requirementsError) {
        console.error('Error loading requirements:', requirementsError);
      } else {
        console.log('Loaded requirements:', requirements);
      }

      // Load client changes
      const { data: changes, error: changesError } = await supabase
        .from('client_changes')
        .select('*')
        .eq('game_id', game.id)
        .order('order', { ascending: true })
        .order('created_at', { ascending: true }); // Fallback to created_at if order is not set

      if (changesError) {
        console.error('Error loading changes:', changesError);
      } else {
        console.log('Loaded changes:', changes);
      }

      // Organize data by session
      const sessionsData = {};
      const changesData = {};
      const bonusData = {};

      if (briefs && briefs.length > 0) {
        briefs.forEach(brief => {
          sessionsData[brief.session_name] = {
            ...brief,
            requirements: requirements?.filter(r => r.session_name === brief.session_name) || [],
            timerDuration: brief.timer_duration || 15,
            brief_text: brief.creative_brief || brief.brief_text || '',
            target_audience: brief.target_audience || ''
          };
        });
        console.log('Organized sessions data:', sessionsData);
      } else {
        console.warn('No briefs found for game:', game.id);
      }

      if (changes) {
        changes.forEach(change => {
          if (!changesData[change.session_name]) {
            changesData[change.session_name] = [];
          }
          if (change.is_bonus) {
            bonusData[change.session_name] = {
              id: change.id,
              text: change.change_text,
              points: change.points
            };
          } else {
            changesData[change.session_name].push({
              id: change.id,
              text: change.change_text,
              points: change.points
            });
          }
        });
      }

      console.log('Loaded sessions:', sessionsData);
      console.log('Loaded requirements:', requirements);
      console.log('Loaded briefs:', briefs);
      console.log('Loaded changes:', changes);
      
      setSessions(sessionsData);
      setClientChanges(changesData);
      setBonusRequirements(bonusData);
      
      // Load bonus requirements from bonus_client_changes table if it exists
      try {
        const { data: bonusChanges } = await supabase
          .from('bonus_client_changes')
          .select('*')
          .eq('game_id', game.id);
        
        if (bonusChanges && bonusChanges.length > 0) {
          bonusChanges.forEach(bonus => {
            bonusData[bonus.session_name] = {
              text: bonus.change_text,
              points: bonus.points
            };
          });
          setBonusRequirements({ ...bonusData });
        }
      } catch (error) {
        console.log('bonus_client_changes table may not exist, using is_bonus flag instead');
      }

      // Load resources
      await loadResources();
      
      // Load game status, designers, and join_code if they exist
      const { data: gameData } = await supabase
        .from('games')
        .select('game_status, designers, join_code')
        .eq('id', game.id)
        .single();
      
      console.log('GameManagement: Loaded game data:', gameData);
      
      // Always update game status, defaulting to 'draft' if not set
      const newStatus = gameData?.game_status || 'draft';
      setGameStatus(newStatus);
      
      // If game is published, trigger navigation immediately
      if (newStatus === 'published' && onNavigateToActiveGame && game?.id) {
        console.log('[GameManagement] Game is published, triggering navigation to ActiveGameView');
        // Use setTimeout to avoid state update conflicts
        setTimeout(() => {
          onNavigateToActiveGame();
        }, 200);
      }

      if (gameData?.join_code) {
        console.log('GameManagement: Loaded join_code:', gameData.join_code);
        setJoinCode(gameData.join_code);
      } else {
        console.log('GameManagement: No join_code found in game data');
      }

      // Load designers from games.designers field
      let normalizedDesigners = [];
      if (gameData?.designers) {
        console.log('GameManagement: Found designers in game data:', gameData.designers);
        // Handle both old format (strings) and new format (objects)
        normalizedDesigners = Array.isArray(gameData.designers)
          ? gameData.designers.map(d => {
              if (typeof d === 'string') {
                return { name: d, avatarUrl: null };
              }
              // If it has firstName/lastName, combine them into name for display
              if (d.firstName || d.lastName) {
                return { 
                  name: `${d.firstName || ''} ${d.lastName || ''}`.trim(), 
                  firstName: d.firstName,
                  lastName: d.lastName,
                  email: d.email,
                  avatarUrl: d.avatarUrl || null 
                };
              }
              return d;
            })
          : [];
      } else {
        console.log('GameManagement: No designers found in game data, checking game_details...');
        // Try to get from game_details
        const { data: details } = await supabase
          .from('game_details')
          .select('designers')
          .eq('game_id', game.id)
          .single();
        
        if (details?.designers) {
          // Handle both old format (strings) and new format (objects)
          normalizedDesigners = Array.isArray(details.designers)
            ? details.designers.map(d => {
                if (typeof d === 'string') {
                  return { name: d, avatarUrl: null };
                }
                // If it has firstName/lastName, combine them into name for display
                if (d.firstName || d.lastName) {
                  return { 
                    name: `${d.firstName || ''} ${d.lastName || ''}`.trim(), 
                    firstName: d.firstName,
                    lastName: d.lastName,
                    email: d.email,
                    avatarUrl: d.avatarUrl || null 
                  };
                }
                return d;
              })
            : [];
        } else if (retryCount === 0) {
          // If no designers found and first attempt, retry
          console.log('No designers found on first attempt, will retry...');
        }
      }

      // Also load participants who joined via join code (both authenticated and email-only)
      const { data: participants, error: participantsError } = await supabase
        .from('game_participants')
        .select('user_id, email, player_name')
        .eq('game_id', game.id);

      // Store participant user IDs for checking join status (filter out nulls for email-only participants)
      if (participants && participants.length > 0) {
        setParticipantUserIds(new Set(participants.filter(p => p.user_id).map(p => p.user_id)));
      } else {
        setParticipantUserIds(new Set());
      }

      if (participantsError) {
        console.error('GameManagement: Error loading participants:', participantsError);
      } else if (participants && participants.length > 0) {
        // Get user profiles for authenticated participants only (filter out null user_ids)
        const userIds = participants.filter(p => p.user_id).map(p => p.user_id);
        let userProfiles = [];
        if (userIds.length > 0) {
          const { data: profilesData, error: profilesError } = await supabase
            .from('user_profiles')
            .select('id, full_name, email')
            .in('id', userIds);
          
          if (profilesError) {
            console.error('GameManagement: Error loading user profiles:', profilesError);
          } else if (profilesData) {
            userProfiles = profilesData;
          }
        }

        // Process authenticated participants (with user_id)
        if (userProfiles && userProfiles.length > 0) {
          // Create a map of user_id to profile for quick lookup
          const profileMap = new Map(userProfiles.map(profile => [profile.id, profile]));

          // First, try to match participants to existing designers and update them with userId
          participants.forEach(participant => {
            const profile = profileMap.get(participant.user_id);
            if (!profile) return; // Skip if profile not found

            const participantName = profile.full_name || profile.email || 'Unknown';
            const participantNameLower = participantName.toLowerCase().trim();
            const participantEmail = profile.email?.toLowerCase().trim();

            // Try to find matching designer by name (exact or partial match)
            let matchedDesignerIndex = -1;
            for (let i = 0; i < normalizedDesigners.length; i++) {
              const designer = normalizedDesigners[i];
              const designerName = typeof designer === 'string' ? designer : designer.name;
              const designerNameLower = designerName?.toLowerCase().trim() || '';
              const designerEmail = typeof designer === 'object' ? designer.email?.toLowerCase().trim() : null;

              // Check for exact name match
              if (designerNameLower === participantNameLower) {
                matchedDesignerIndex = i;
                break;
              }
              
              // Check for partial name match (one contains the other)
              if (designerNameLower && participantNameLower) {
                const designerParts = designerNameLower.split(' ');
                const participantParts = participantNameLower.split(' ');
                // Check if first name matches
                if (designerParts[0] === participantParts[0] || 
                    designerParts[designerParts.length - 1] === participantParts[participantParts.length - 1]) {
                  matchedDesignerIndex = i;
                  break;
                }
              }

              // Check for email match
              if (designerEmail && participantEmail && designerEmail === participantEmail) {
                matchedDesignerIndex = i;
                break;
              }
            }

            // If we found a match, update the designer entry with userId
            if (matchedDesignerIndex >= 0) {
              const matchedDesigner = normalizedDesigners[matchedDesignerIndex];
              // Update the designer entry to include userId
              // IMPORTANT: Always use the participant's profile name since that's their authenticated identity
              if (typeof matchedDesigner === 'object') {
                normalizedDesigners[matchedDesignerIndex] = {
                  ...matchedDesigner,
                  userId: participant.user_id,
                  // Always use the participant's profile name (their authenticated identity)
                  name: participantName,
                  email: profile.email || matchedDesigner.email
                };
              } else {
                // Convert string to object with userId
                // Use participant's profile name (their authenticated identity)
                normalizedDesigners[matchedDesignerIndex] = {
                  name: participantName,
                  userId: participant.user_id,
                  email: profile.email || null,
                  avatarUrl: null
                };
              }
            } else {
              // No match found - add as new designer
              normalizedDesigners.push({
                name: participantName,
                email: profile.email || null,
                avatarUrl: null,
                userId: participant.user_id,
                isParticipant: true
              });
            }
          });
        }
        
        // Add email-only participants (those without user_id) to designers list
        const emailOnlyParticipants = participants.filter(p => !p.user_id && p.email && p.player_name);
        for (const emailParticipant of emailOnlyParticipants) {
          // Check if this email-only participant is already in the designers list
          const alreadyExists = normalizedDesigners.some(d => 
            (typeof d === 'object' && (d.email === emailParticipant.email || d.name === emailParticipant.player_name)) ||
            (typeof d === 'string' && d === emailParticipant.player_name)
          );

          if (!alreadyExists) {
            normalizedDesigners.push({
              name: emailParticipant.player_name,
              email: emailParticipant.email,
              userId: null, // No user_id for email-only participants
              avatarUrl: null,
              isParticipant: true
            });
          }
        }
      }

      console.log('GameManagement: Final normalized designers (including participants):', normalizedDesigners);
      setDesigners(normalizedDesigners);
      
      // If no data found and first attempt, schedule a retry
      if (retryCount === 0 && (!briefs || briefs.length === 0) && (!gameData?.designers || gameData.designers.length === 0)) {
        console.log('No data found on first attempt, retrying in 1.5 seconds...');
        setTimeout(() => {
          loadGameData(1);
        }, 1500);
        return;
      }
    } catch (error) {
      console.error('Error loading game data:', error);
      // Retry on error if first attempt
      if (retryCount === 0) {
        console.log('Error on first attempt, retrying in 2 seconds...');
        setTimeout(() => {
          loadGameData(1);
        }, 2000);
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResourcesChange = async () => {
    await loadResources();
  };

  // Save creative brief
  const handleSaveBrief = async (sessionKey, newBrief) => {
    if (!game?.id || gameStatus !== 'draft') return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('session_briefs')
        .update({ brief_text: newBrief.trim() })
        .eq('game_id', game.id)
        .eq('session_name', sessionKey);

      if (error) throw error;

      await loadGameData();
      setEditingSection(null);
    } catch (error) {
      console.error('Error saving brief:', error);
      alert('Failed to save creative brief. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Save target audience
  const handleSaveAudience = async (sessionKey, newAudience) => {
    if (!game?.id || gameStatus !== 'draft') return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('session_briefs')
        .update({ target_audience: newAudience.trim() })
        .eq('game_id', game.id)
        .eq('session_name', sessionKey);

      if (error) throw error;

      await loadGameData();
      setEditingSection(null);
    } catch (error) {
      console.error('Error saving audience:', error);
      alert('Failed to save target audience. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Save timer duration
  const handleSaveTimerDuration = async (sessionKey, newDuration) => {
    if (!game?.id || gameStatus !== 'draft') return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('session_briefs')
        .update({ timer_duration: parseInt(newDuration) || 15 })
        .eq('game_id', game.id)
        .eq('session_name', sessionKey);

      if (error) throw error;

      await loadGameData();
      setEditingSection(null);
    } catch (error) {
      console.error('Error saving timer duration:', error);
      alert('Failed to save timer duration. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Save session name
  const handleSaveSessionName = async (sessionKey, newName) => {
    if (!game?.id || gameStatus !== 'draft') return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('session_briefs')
        .update({ session_display_name: newName.trim() })
        .eq('game_id', game.id)
        .eq('session_name', sessionKey);

      if (error) throw error;

      await loadGameData();
      setEditingSessionName(null);
    } catch (error) {
      console.error('Error saving session name:', error);
      alert('Failed to save session name. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Save requirement changes
  const handleSaveRequirement = async (sessionKey, requirementIndex, newText) => {
    if (!game?.id || gameStatus !== 'draft') return;
    
    setSaving(true);
    try {
      const session = sessions[sessionKey];
      if (!session || !session.requirements) {
        console.error('Session or requirements not found:', { sessionKey, session });
        return;
      }

      const requirement = session.requirements[requirementIndex];
      if (!requirement) {
        console.error('Requirement not found:', { requirementIndex, requirements: session.requirements });
        return;
      }

      if (!requirement.id) {
        console.error('Requirement missing id:', requirement);
        alert('Error: Requirement ID not found. Please refresh the page and try again.');
        return;
      }

      console.log('Updating requirement:', { 
        id: requirement.id, 
        newText: newText.trim(), 
        gameId: game.id,
        requirementGameId: requirement.game_id,
        fullRequirement: requirement
      });

      // First, verify the requirement exists
      const { data: existingReq, error: checkError } = await supabase
        .from('session_requirements')
        .select('*')
        .eq('id', requirement.id)
        .single();

      if (checkError) {
        console.error('Error checking requirement:', checkError);
        // If requirement doesn't exist, we can't update it - this shouldn't happen
        throw new Error(`Requirement not found: ${checkError.message}`);
      }

      if (!existingReq) {
        throw new Error('Requirement not found in database.');
      }

      console.log('Found existing requirement:', existingReq);

      // Try update with just ID first
      let updateResult = await supabase
        .from('session_requirements')
        .update({ requirement_text: newText.trim() })
        .eq('id', requirement.id)
        .select();

      console.log('First update attempt result:', updateResult);

      // If that fails and we have a game_id, try with game_id filter
      if ((!updateResult.data || updateResult.data.length === 0) && existingReq.game_id) {
        console.log('First attempt failed, trying with game_id filter...');
        updateResult = await supabase
          .from('session_requirements')
          .update({ requirement_text: newText.trim() })
          .eq('id', requirement.id)
          .eq('game_id', existingReq.game_id)
          .select();
        
        console.log('Second update attempt result:', updateResult);
      }

      // If still no rows updated, try delete and recreate approach
      if (!updateResult.data || updateResult.data.length === 0) {
        console.log('Update failed, trying delete and recreate approach...');
        
        // Delete the old requirement
        const deleteResult = await supabase
          .from('session_requirements')
          .delete()
          .eq('id', requirement.id);
        
        if (deleteResult.error) {
          console.error('Delete error:', deleteResult.error);
          throw new Error(`Failed to delete requirement: ${deleteResult.error.message}`);
        }
        
        // Recreate with new text
        const insertResult = await supabase
          .from('session_requirements')
          .insert({
            game_id: existingReq.game_id || game.id,
            session_name: existingReq.session_name || sessionKey,
            requirement_text: newText.trim()
          })
          .select();
        
        if (insertResult.error) {
          console.error('Insert error:', insertResult.error);
          throw new Error(`Failed to recreate requirement: ${insertResult.error.message}`);
        }
        
        console.log('Delete and recreate succeeded:', insertResult.data);
        updateResult = insertResult;
      }

      if (updateResult.error) {
        console.error('Supabase update error:', updateResult.error);
        throw updateResult.error;
      }

      if (!updateResult.data || updateResult.data.length === 0) {
        throw new Error('No rows were updated. The requirement may not exist or you may not have permission to update it.');
      }

      console.log('Final update result:', updateResult.data);

      // Reload game data to reflect changes
      await loadGameData();
      setEditingRequirement(null);
    } catch (error) {
      console.error('Error saving requirement:', error);
      alert(`Failed to save requirement: ${error.message || 'Please try again.'}`);
    } finally {
      setSaving(false);
    }
  };

  // Delete requirement
  const handleDeleteRequirement = async (sessionKey, requirementIndex) => {
    if (!game?.id || gameStatus !== 'draft') return;
    if (!window.confirm('Are you sure you want to delete this requirement?')) return;

    setSaving(true);
    try {
      const session = sessions[sessionKey];
      if (!session || !session.requirements) return;

      const requirement = session.requirements[requirementIndex];
      if (!requirement) return;

      const { error } = await supabase
        .from('session_requirements')
        .delete()
        .eq('id', requirement.id);

      if (error) throw error;

      await loadGameData();
    } catch (error) {
      console.error('Error deleting requirement:', error);
      alert('Failed to delete requirement. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Add new requirement
  const handleAddRequirement = async (sessionKey, requirementText) => {
    if (!game?.id || gameStatus !== 'draft' || !requirementText?.trim()) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('session_requirements')
        .insert([{
          game_id: game.id,
          session_name: sessionKey,
          requirement_text: requirementText.trim()
        }]);

      if (error) throw error;

      await loadGameData();
    } catch (error) {
      console.error('Error adding requirement:', error);
      alert('Failed to add requirement. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Save client change
  const handleSaveClientChange = async (sessionKey, changeIndex, updatedChange) => {
    if (!game?.id || gameStatus !== 'draft') return;

    setSaving(true);
    try {
      const changes = clientChanges[sessionKey] || [];
      const change = changes[changeIndex];
      if (!change || !change.id) {
        console.error('No change found or missing id:', { change, changeIndex });
        alert('Failed to save client change: Change not found.');
        return;
      }

      console.log('Updating client change:', {
        id: change.id,
        sessionKey,
        changeIndex,
        newText: updatedChange.text?.trim(),
        newPoints: updatedChange.points || 1
      });

      // First, verify the change exists
      const { data: existingChange, error: fetchError } = await supabase
        .from('client_changes')
        .select('id, change_text, points, timing_minutes, is_bonus, game_id, session_name, order')
        .eq('id', change.id)
        .single();

      if (fetchError || !existingChange) {
        console.error('Client change not found:', { id: change.id, error: fetchError });
        throw new Error('Client change not found in database');
      }

      console.log('Found existing client change:', existingChange);

      // Try to update by id only (id should be unique)
      // Preserve the order field to maintain position
      const { data: updatedData, error: updateError } = await supabase
        .from('client_changes')
        .update({
          change_text: updatedChange.text.trim(),
          points: updatedChange.points || 1,
          order: existingChange.order ?? null // Preserve existing order
        })
        .eq('id', change.id)
        .select();

      if (updateError) {
        console.error('Update error:', updateError);
        throw updateError;
      }

      if (!updatedData || updatedData.length === 0) {
        console.warn('No rows were updated. Attempting fallback (delete and recreate)...');
        
        // Fallback: Delete and re-insert, preserving order
        const { error: deleteError } = await supabase
          .from('client_changes')
          .delete()
          .eq('id', change.id);

        if (deleteError) {
          console.error('Delete error during fallback:', deleteError);
          throw new Error('Failed to update client change: Could not delete existing record.');
        }

        const { error: insertError } = await supabase
          .from('client_changes')
          .insert([{
            id: change.id, // Try to preserve the same ID
            game_id: existingChange.game_id || game.id,
            session_name: existingChange.session_name || sessionKey,
            change_text: updatedChange.text.trim(),
            points: updatedChange.points || 1,
            is_bonus: existingChange.is_bonus || false,
            order: existingChange.order ?? null // Preserve order
          }]);

        if (insertError) {
          console.error('Insert error during fallback:', insertError);
          // If preserving ID fails, try without ID (let DB generate new one)
          const { error: insertError2 } = await supabase
            .from('client_changes')
            .insert([{
              game_id: existingChange.game_id || game.id,
              session_name: existingChange.session_name || sessionKey,
              change_text: updatedChange.text.trim(),
              points: updatedChange.points || 1,
              is_bonus: existingChange.is_bonus || false,
              order: existingChange.order ?? null // Preserve order
            }]);

          if (insertError2) {
            throw new Error(`Failed to save client change: ${insertError2.message}`);
          }
        }

        console.log('Delete and recreate succeeded');
      } else {
        console.log('Update succeeded:', updatedData);
      }

      await loadGameData();
      setEditingClientChange(null);
      setEditingClientChangeValues(null);
    } catch (error) {
      console.error('Error saving client change:', error);
      alert(`Failed to save client change: ${error.message || 'Please try again.'}`);
    } finally {
      setSaving(false);
    }
  };

  // Delete client change
  const handleDeleteClientChange = async (sessionKey, changeIndex) => {
    if (!game?.id || gameStatus !== 'draft') return;
    if (!window.confirm('Are you sure you want to delete this client change?')) return;

    setSaving(true);
    try {
      const changes = clientChanges[sessionKey] || [];
      const change = changes[changeIndex];
      if (!change || !change.id) return;

      const { error } = await supabase
        .from('client_changes')
        .delete()
        .eq('id', change.id);

      if (error) throw error;

      await loadGameData();
    } catch (error) {
      console.error('Error deleting client change:', error);
      alert('Failed to delete client change. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Add new client change
  const handleAddClientChange = async (sessionKey, changeText, points = 1) => {
    if (!game?.id || gameStatus !== 'draft' || !changeText?.trim()) return;

    setSaving(true);
    try {
      // Get the current max order for this session to append at the end
      const { data: existingChanges } = await supabase
        .from('client_changes')
        .select('order')
        .eq('game_id', game.id)
        .eq('session_name', sessionKey)
        .eq('is_bonus', false)
        .order('order', { ascending: false })
        .limit(1);

      const nextOrder = existingChanges && existingChanges.length > 0 
        ? (existingChanges[0].order ?? 0) + 1 
        : 0;

      const { error } = await supabase
        .from('client_changes')
        .insert([{
          game_id: game.id,
          session_name: sessionKey,
          change_text: changeText.trim(),
          points: points || 1,
          is_bonus: false,
          order: nextOrder
        }]);

      if (error) throw error;

      await loadGameData();
    } catch (error) {
      console.error('Error adding client change:', error);
      alert('Failed to add client change. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Save bonus change
  const handleSaveBonus = async (sessionKey, updatedBonus) => {
    if (!game?.id || gameStatus !== 'draft') return;

    setSaving(true);
    try {
      const bonus = bonusRequirements[sessionKey];
      
      if (bonus && bonus.id) {
        // Update existing bonus
        console.log('Updating bonus change:', {
          id: bonus.id,
          sessionKey,
          newText: updatedBonus.text?.trim(),
          newPoints: updatedBonus.points || 4
        });

        // First, verify the bonus exists
        const { data: existingBonus, error: fetchError } = await supabase
          .from('client_changes')
          .select('id, change_text, points, timing_minutes, is_bonus, game_id, session_name, order')
          .eq('id', bonus.id)
          .single();

        if (fetchError || !existingBonus) {
          console.error('Bonus change not found:', { id: bonus.id, error: fetchError });
          throw new Error('Bonus change not found in database');
        }

        console.log('Found existing bonus change:', existingBonus);

        // Try to update by id only (id should be unique)
        // Preserve the order field to maintain position
        const { data: updatedData, error: updateError } = await supabase
          .from('client_changes')
          .update({
            change_text: updatedBonus.text.trim(),
            points: updatedBonus.points || 4,
            order: existingBonus.order ?? null // Preserve existing order
          })
          .eq('id', bonus.id)
          .select();

        if (updateError) {
          console.error('Update error:', updateError);
          throw updateError;
        }

        if (!updatedData || updatedData.length === 0) {
          console.warn('No rows were updated. Attempting fallback (delete and recreate)...');
          
          // Fallback: Delete and re-insert, preserving order
          const { error: deleteError } = await supabase
            .from('client_changes')
            .delete()
            .eq('id', bonus.id);

          if (deleteError) {
            console.error('Delete error during fallback:', deleteError);
            throw new Error('Failed to update bonus change: Could not delete existing record.');
          }

          const { error: insertError } = await supabase
            .from('client_changes')
            .insert([{
              id: bonus.id, // Try to preserve the same ID
              game_id: existingBonus.game_id || game.id,
              session_name: existingBonus.session_name || sessionKey,
              change_text: updatedBonus.text.trim(),
              points: updatedBonus.points || 4,
              is_bonus: true,
              order: existingBonus.order ?? null // Preserve order
            }]);

          if (insertError) {
            console.error('Insert error during fallback:', insertError);
            // If preserving ID fails, try without ID (let DB generate new one)
            const { error: insertError2 } = await supabase
              .from('client_changes')
              .insert([{
                game_id: existingBonus.game_id || game.id,
                session_name: existingBonus.session_name || sessionKey,
                change_text: updatedBonus.text.trim(),
                points: updatedBonus.points || 4,
                is_bonus: true,
                order: existingBonus.order ?? null // Preserve order
              }]);

            if (insertError2) {
              throw new Error(`Failed to save bonus change: ${insertError2.message}`);
            }
          }

          console.log('Delete and recreate succeeded');
        } else {
          console.log('Update succeeded:', updatedData);
        }
      } else {
        // Create new bonus
        const { error } = await supabase
          .from('client_changes')
          .insert([{
            game_id: game.id,
            session_name: sessionKey,
            change_text: updatedBonus.text.trim(),
            points: updatedBonus.points || 4,
            is_bonus: true,
            order: 9999 // Bonus changes always go at the end
          }]);
        
        if (error) throw error;
      }

      await loadGameData();
      setEditingBonus(null);
      setEditingBonusValues(null);
    } catch (error) {
      console.error('Error saving bonus change:', error);
      alert(`Failed to save bonus change: ${error.message || 'Please try again.'}`);
    } finally {
      setSaving(false);
    }
  };

  const generateJoinCode = () => {
    // Generate a 6-character code excluding confusing characters (0, O, I, 1)
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const handlePublishGame = async () => {
    console.log('[PUBLISH] Publishing game...', { gameId: game?.id, currentStatus: gameStatus });
    
    try {
      // Generate join code if it doesn't exist
      let joinCode = game?.join_code;
      if (!joinCode) {
        joinCode = generateJoinCode();
      }
      
      const { error, data } = await supabase
        .from('games')
        .update({ 
          game_status: 'published',
          join_code: joinCode
        })
        .eq('id', game.id)
        .select();
      
      if (error) {
        console.error('[PUBLISH] Database update error:', error);
        
        // If code collision, try again with new code
        if (error.code === '23505') { // Unique constraint violation
          const newCode = generateJoinCode();
          const { error: retryError } = await supabase
            .from('games')
            .update({ 
              game_status: 'published',
              join_code: newCode
            })
            .eq('id', game.id);
          
          if (retryError) {
            throw retryError;
          }
          joinCode = newCode;
        } else if (error.message && error.message.includes('column') && error.message.includes('join_code')) {
          // Column doesn't exist - migration not run
          alert('Error: The join_code column is missing. Please run the migration file add_join_code_column.sql in your Supabase SQL editor.');
          throw error;
        } else {
          throw error;
        }
      }
      
      setGameStatus('published');
      setJoinCode(joinCode);
      
      console.log('[PUBLISH] Game published successfully, navigating to ActiveGameView...');
      
      // CRITICAL: Navigate to ActiveGameView after publishing
      if (onNavigateToActiveGame && game?.id) {
        onNavigateToActiveGame();
        return; // Exit early to prevent further processing/re-renders in this component
      } else {
        console.error('[PUBLISH] Cannot navigate - missing callback or game ID');
        await loadGameData();
      }
    } catch (error) {
      console.error('[PUBLISH] Error publishing game:', error);
      alert('Failed to publish game. Please try again.');
    }
  };

  if (loading) {
    return (
      <>
        <CardNav
          logo="/rollerCoaster_logo.svg"
          logoAlt="Client Roller Coaster Logo"
          logoText={game?.game_name || 'Game Management'}
          baseColor="#242323"
          menuColor="#fff"
          showBackButton={true}
          backButtonText="← Back"
          onBackClick={onBack}
          showHamburger={false}
          showButton={false}
          items={[]}
        />
        <main 
          className="app-main" 
          data-view="game-management"
          style={{ 
            paddingTop: 'calc(2em + 60px + 2rem)',
            backgroundColor: '#060010',
            minHeight: '100vh'
          }}
        >
          <div style={{ 
            background: 'transparent', 
            boxShadow: 'none', 
            textAlign: 'center',
            padding: '2rem',
            color: '#ffffff'
          }}>
            <p>Loading game details...</p>
          </div>
        </main>
      </>
    );
  }

  return (
    <>

      <CardNav
        logo="/rollerCoaster_logo.svg"
        logoAlt="Client Roller Coaster Logo"
        logoText={game?.game_name || 'Game Management'}
        showDraftBadge={gameStatus === 'draft'}
        baseColor="#242323"
        menuColor="#fff"
        showBackButton={true}
        backButtonText="← Back"
        onBackClick={onBack}
        showHamburger={false}
        showButton={gameStatus === 'draft'}
        buttonBgColor="#D702F6"
        buttonTextColor="#ffffff"
        buttonText="Publish Game"
        onButtonClick={handlePublishGame}
        items={[]}
      />
      <main 
        className="app-main" 
        data-view="game-management"
        style={{ 
          paddingTop: 'calc(2em + 60px + 2rem)',
          backgroundColor: '#060010',
          minHeight: '100vh'
        }}
      >
        <div className="section" style={{ background: 'transparent', boxShadow: 'none', padding: 0 }}>
          {/* Review & Score Button - Show for published/started/finished games */}
          {(gameStatus === 'published' || gameStatus === 'started' || gameStatus === 'finished') && onNavigateToReview && (
            <div style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={() => onNavigateToReview(game)}
                style={{ 
                  backgroundColor: '#D702F6',
                  border: '2px solid #D702F6',
                  color: '#ffffff',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '1rem',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  transition: 'background-color 0.3s ease, border-color 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#C001E5';
                  e.target.style.borderColor = '#C001E5';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#D702F6';
                  e.target.style.borderColor = '#D702F6';
                }}
              >
                {gameStatus === 'finished' ? 'View Scores' : 'Review & Score'}
              </button>
            </div>
          )}
          <div style={{ 
            background: 'rgba(255, 255, 255, 0.05)', 
            borderRadius: '12px', 
            padding: '2rem',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>

            {/* Game Summary Section */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ color: '#ffffff', marginBottom: '1rem', fontSize: '1.25rem' }}>Game Summary</h3>
              <div style={{
                background: 'rgba(255, 255, 255, 0.03)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '8px',
                padding: '1.5rem'
              }}>
                {/* First Row: Number of Sessions/Total Designers/Designer Chips and Join Code (Title + Content) */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '1.5rem',
                  alignItems: 'flex-start'
                }}>
                  <div>
                    <div style={{ marginBottom: '0.75rem' }}>
                  <strong style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Number of Sessions:</strong>{' '}
                  <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{Object.keys(sessions).length}</span>
                </div>
                    <div style={{ marginBottom: designers.length > 0 ? '0.75rem' : '0' }}>
                  <strong style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Total Designers:</strong>{' '}
                  <span style={{ color: 'rgba(255, 255, 255, 0.7)' }}>{designers.length}</span>
                </div>
                {designers.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                      {designers.map((designer, index) => {
                        const designerName = typeof designer === 'string' ? designer : designer.name;
                        const avatarUrl = getAvatarUrl(designer);
                        const initials = getDesignerInitials(designer);
                          // Check if this designer has joined (is a participant)
                          const hasJoined = designer?.userId && participantUserIds.has(designer.userId);
                        return (
                          <div
                            key={index}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                                background: hasJoined 
                                  ? 'rgba(76, 175, 80, 0.2)' 
                                  : 'rgba(215, 2, 246, 0.2)',
                                color: hasJoined ? '#4CAF50' : '#D702F6',
                              padding: '0.5rem 1rem',
                              borderRadius: '6px',
                              fontSize: '0.875rem',
                                fontWeight: 500,
                                border: hasJoined 
                                  ? '1px solid rgba(76, 175, 80, 0.3)' 
                                  : 'none'
                            }}
                          >
                            {/* Avatar */}
                              <div style={{ position: 'relative', flexShrink: 0 }}>
                            <div
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                    border: hasJoined 
                                      ? '2px solid #4CAF50' 
                                      : '2px solid rgba(215, 2, 246, 0.5)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                overflow: 'hidden',
                                background: avatarUrl 
                                  ? `url(${avatarUrl}) center/cover`
                                      : hasJoined
                                        ? 'rgba(76, 175, 80, 0.3)'
                                  : 'rgba(215, 2, 246, 0.3)',
                                color: '#ffffff',
                                fontSize: '0.75rem',
                                fontWeight: 600
                              }}
                            >
                              {!avatarUrl && initials}
                                </div>
                            </div>
                            {/* Name */}
                            <span>{designerName}</span>
                              {/* Checkmark */}
                              {hasJoined && (
                                <span style={{ 
                                  color: '#4CAF50', 
                                  fontSize: '1rem',
                                  fontWeight: 'bold',
                                  marginLeft: '0.25rem'
                                }}>✓</span>
                              )}
                          </div>
                        );
                      })}
                    </div>
                    )}
                  </div>
                  {joinCode && (gameStatus === 'published' || gameStatus === 'started') && (
                    <div>
                      <strong style={{ color: 'rgba(255, 255, 255, 0.9)', display: 'block', marginBottom: '0.5rem' }}>Join Code:</strong>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        background: 'rgba(215, 2, 246, 0.1)',
                        border: '2px solid #D702F6',
                        borderRadius: '8px',
                        padding: '1rem',
                        justifyContent: 'space-between'
                      }}>
                        <code style={{
                          fontSize: '1.5rem',
                          fontWeight: 700,
                          letterSpacing: '0.2em',
                          color: '#D702F6',
                          fontFamily: 'monospace'
                        }}>
                          {joinCode}
                        </code>
                        <button
                          onClick={() => {
                            const joinUrl = `${window.location.origin}/join/${joinCode}`;
                            navigator.clipboard.writeText(joinUrl).then(() => {
                              alert('Join link copied to clipboard!');
                            }).catch(() => {
                              // Fallback: copy code only
                              navigator.clipboard.writeText(joinCode).then(() => {
                                alert('Join code copied to clipboard!');
                              });
                            });
                          }}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#D702F6',
                            border: 'none',
                            borderRadius: '6px',
                            color: '#ffffff',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.background = '#C001E5';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.background = '#D702F6';
                          }}
                        >
                          Copy Link
                        </button>
                      </div>
                      <p style={{
                        color: 'rgba(255, 255, 255, 0.6)',
                        fontSize: '0.875rem',
                        marginTop: '0.75rem',
                        marginBottom: 0
                      }}>
                        Share this code or link with designers to join the game
                      </p>
                  </div>
                )}
                </div>
              </div>
            </div>

            {/* Timer Cards Section - Show timer for each session */}
            {Object.keys(sessions).length > 0 && (
              <div style={{ marginBottom: '2rem' }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: Object.keys(sessions).length === 1 ? '1fr' : '1fr 1fr',
                  gap: '2rem'
                }}>
                  {Object.entries(sessions).map(([sessionKey, session], index) => {
                    const timerDuration = session.timerDuration || 15;
                    const sessionDisplayName = session.session_display_name || (sessionKey === 'sessionA' ? 'Session A' : sessionKey === 'sessionB' ? 'Session B' : sessionKey);
                    const isEditingTimer = editingSection?.sessionKey === sessionKey && editingSection?.section === 'timer';
                    
                    return (
                      <div
                        key={sessionKey}
                        style={{
                          background: 'rgba(255, 255, 255, 0.05)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '12px',
                          padding: '2rem',
                          textAlign: 'center',
                          position: 'relative'
                        }}
                      >
                        <h2 style={{ color: '#fff', marginBottom: '1rem', fontSize: '1.5rem' }}>
                          {sessionDisplayName}
                        </h2>
                        {isEditingTimer ? (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                              type="number"
                              defaultValue={timerDuration}
                              min="1"
                              onBlur={(e) => {
                                const newDuration = e.target.value;
                                if (parseInt(newDuration) !== timerDuration) {
                                  handleSaveTimerDuration(sessionKey, newDuration);
                                } else {
                                  setEditingSection(null);
                                }
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.target.blur();
                                }
                                if (e.key === 'Escape') {
                                  setEditingSection(null);
                                }
                              }}
                              style={{
                                width: '120px',
                                background: 'rgba(255, 255, 255, 0.1)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                borderRadius: '6px',
                                padding: '0.75rem',
                                color: '#ffffff',
                                fontSize: '2.5rem',
                                fontWeight: 700,
                                fontFamily: 'monospace',
                                textAlign: 'center'
                              }}
                              autoFocus
                            />
                            <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '1rem' }}>minutes</span>
                          </div>
                        ) : (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
                            <NeonCountdown
                              size="lg"
                              seconds={(timerDuration || 0) * 60}
                            />
                            {gameStatus === 'draft' && (
                              <button
                                onClick={() => setEditingSection({ sessionKey, section: 'timer' })}
                                style={{
                                  padding: '0.5rem',
                                  background: 'transparent',
                                  border: 'none',
                                  color: 'rgba(255, 255, 255, 0.7)',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '32px',
                                  height: '32px'
                                }}
                                title="Edit timer duration"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="rgba(255, 255, 255, 0.7)"><path d="M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Zm640-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z"/></svg>
                              </button>
                            )}
                            {/* Start Game button - Show for first session when game is published */}
                            {gameStatus === 'started' && index === 0 && onStartGame && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onStartGame();
                                }}
                                style={{
                                  background: '#D702F6',
                                  color: '#ffffff',
                                  border: 'none',
                                  borderRadius: '8px',
                                  padding: '0.75rem 2rem',
                                  fontSize: '1rem',
                                  fontWeight: 600,
                                  cursor: 'pointer',
                                  transition: 'all 0.2s ease',
                                  boxShadow: '0 4px 12px rgba(215, 2, 246, 0.3)'
                                }}
                                onMouseOver={(e) => {
                                  e.target.style.background = '#E91E63';
                                  e.target.style.boxShadow = '0 6px 16px rgba(215, 2, 246, 0.4)';
                                }}
                                onMouseOut={(e) => {
                                  e.target.style.background = '#D702F6';
                                  e.target.style.boxShadow = '0 4px 12px rgba(215, 2, 246, 0.3)';
                                }}
                              >
                                Start Game
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Sessions Section */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ color: '#ffffff', marginBottom: '1rem', fontSize: '1.25rem' }}>Sessions</h3>
              
              {Object.keys(sessions).length === 0 ? (
                <div style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  textAlign: 'center',
                  color: 'rgba(255, 255, 255, 0.5)'
                }}>
                  No sessions configured yet
                </div>
              ) : (
                Object.keys(sessions).map((sessionKey) => {
                const session = sessions[sessionKey];
                const changes = clientChanges[sessionKey] || [];
                const bonus = bonusRequirements[sessionKey];
                
                return (
                  <div key={sessionKey} style={{
                    background: 'rgba(255, 255, 255, 0.03)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    padding: '1.5rem',
                    marginBottom: '1.5rem'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                      {editingSessionName === sessionKey ? (
                        <input
                          type="text"
                          defaultValue={session.session_display_name || (sessionKey === 'sessionA' ? 'Session A' : 'Session B')}
                          onBlur={(e) => {
                            const newName = e.target.value;
                            const currentName = session.session_display_name || (sessionKey === 'sessionA' ? 'Session A' : 'Session B');
                            if (newName.trim() !== currentName) {
                              handleSaveSessionName(sessionKey, newName);
                            } else {
                              setEditingSessionName(null);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.target.blur();
                            }
                            if (e.key === 'Escape') {
                              setEditingSessionName(null);
                            }
                          }}
                          style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '6px',
                            padding: '0.5rem',
                            color: '#ffffff',
                            fontSize: '1.1rem',
                            fontFamily: 'inherit',
                            fontWeight: 600,
                            flex: 1,
                            maxWidth: '400px'
                          }}
                          autoFocus
                        />
                      ) : (
                        <>
                          <h4 style={{ color: '#ffffff', fontSize: '1.1rem', margin: 0 }}>
                      {session.session_display_name || (sessionKey === 'sessionA' ? 'Session A' : 'Session B')}
                    </h4>
                          {gameStatus === 'draft' && (
                            <button
                              onClick={() => setEditingSessionName(sessionKey)}
                              style={{
                                padding: '0.25rem',
                                background: 'transparent',
                                border: 'none',
                                color: 'rgba(255, 255, 255, 0.7)',
                                fontSize: '1rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '24px',
                                height: '24px'
                              }}
                              title="Edit session name"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="rgba(255, 255, 255, 0.7)"><path d="M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Zm640-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z"/></svg>
                            </button>
                          )}
                        </>
                      )}
                    </div>
                    
                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <strong style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Creative Brief:</strong>
                        {gameStatus === 'draft' && (
                          <button
                            onClick={() => setEditingSection(editingSection?.sessionKey === sessionKey && editingSection?.section === 'brief' ? null : { sessionKey, section: 'brief' })}
                            style={{
                              padding: '0.25rem',
                              background: 'transparent',
                              border: 'none',
                              color: 'rgba(255, 255, 255, 0.7)',
                              fontSize: '1rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '24px',
                              height: '24px'
                            }}
                            title="Edit creative brief"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="rgba(255, 255, 255, 0.7)"><path d="M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Zm640-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z"/></svg>
                          </button>
                        )}
                      </div>
                      {editingSection?.sessionKey === sessionKey && editingSection?.section === 'brief' ? (
                        <textarea
                          defaultValue={session.brief_text || session.creative_brief || ''}
                          onBlur={(e) => {
                            const newBrief = e.target.value;
                            if (newBrief.trim() !== (session.brief_text || session.creative_brief || '')) {
                              handleSaveBrief(sessionKey, newBrief);
                            } else {
                              setEditingSection(null);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              setEditingSection(null);
                            }
                          }}
                          style={{
                            width: '100%',
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '6px',
                            padding: '0.5rem',
                            color: '#ffffff',
                            fontSize: '0.875rem',
                            fontFamily: 'inherit',
                            resize: 'vertical',
                            minHeight: '100px'
                          }}
                          autoFocus
                        />
                      ) : (
                      <p style={{ 
                        color: 'rgba(255, 255, 255, 0.7)', 
                        marginTop: '0.5rem', 
                        lineHeight: '1.6', 
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word',
                        wordBreak: 'break-word',
                        maxWidth: '100%',
                        overflow: 'hidden'
                      }}>
                          {session.brief_text || session.creative_brief || 'Not set'}
                      </p>
                      )}
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <strong style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Target Audience:</strong>
                        {gameStatus === 'draft' && (
                          <button
                            onClick={() => setEditingSection(editingSection?.sessionKey === sessionKey && editingSection?.section === 'audience' ? null : { sessionKey, section: 'audience' })}
                            style={{
                              padding: '0.25rem',
                              background: 'transparent',
                              border: 'none',
                              color: 'rgba(255, 255, 255, 0.7)',
                              fontSize: '1rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '24px',
                              height: '24px'
                            }}
                            title="Edit target audience"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="rgba(255, 255, 255, 0.7)"><path d="M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Zm640-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z"/></svg>
                          </button>
                        )}
                      </div>
                      {editingSection?.sessionKey === sessionKey && editingSection?.section === 'audience' ? (
                        <textarea
                          defaultValue={session.target_audience || ''}
                          onBlur={(e) => {
                            const newAudience = e.target.value;
                            if (newAudience.trim() !== (session.target_audience || '')) {
                              handleSaveAudience(sessionKey, newAudience);
                            } else {
                              setEditingSection(null);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') {
                              setEditingSection(null);
                            }
                          }}
                          style={{
                            width: '100%',
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: '6px',
                            padding: '0.5rem',
                            color: '#ffffff',
                            fontSize: '0.875rem',
                            fontFamily: 'inherit',
                            resize: 'vertical',
                            minHeight: '100px'
                          }}
                          autoFocus
                        />
                      ) : (
                      <p style={{ color: 'rgba(255, 255, 255, 0.7)', marginTop: '0.5rem', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                        {session.target_audience || 'Not set'}
                      </p>
                      )}
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <strong style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Requirements:</strong>
                        {gameStatus === 'draft' && (
                          <>
                            <button
                              onClick={() => setEditingSection(editingSection?.sessionKey === sessionKey && editingSection?.section === 'requirements' ? null : { sessionKey, section: 'requirements' })}
                              style={{
                                padding: '0.25rem',
                                background: 'transparent',
                                border: 'none',
                                color: 'rgba(255, 255, 255, 0.7)',
                                fontSize: '1rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '24px',
                                height: '24px'
                              }}
                              title="Edit requirements"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="rgba(255, 255, 255, 0.7)"><path d="M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Zm640-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z"/></svg>
                            </button>
                            {editingSection?.sessionKey === sessionKey && editingSection?.section === 'requirements' && (
                              <button
                                onClick={() => {
                                  // Open a prompt to add new requirement
                                  const newReq = window.prompt('Enter new requirement:');
                                  if (newReq && newReq.trim()) {
                                    handleAddRequirement(sessionKey, newReq.trim());
                                  }
                                }}
                                style={{
                                  padding: '0.25rem 0.75rem',
                                  background: 'rgba(215, 2, 246, 0.2)',
                                  border: '1px solid #D702F6',
                                  borderRadius: '6px',
                                  color: '#D702F6',
                                  fontSize: '0.875rem',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '0.25rem'
                                }}
                              >
                                + Add
                              </button>
                            )}
                          </>
                        )}
                    </div>

                      {session.requirements && session.requirements.length > 0 ? (
                        <ul style={{ color: 'rgba(255, 255, 255, 0.7)', marginTop: '0.5rem', paddingLeft: 0, lineHeight: '1.6', listStyle: 'none' }}>
                          {session.requirements.map((req, idx) => {
                            const isEditing = editingRequirement?.sessionKey === sessionKey && editingRequirement?.index === idx;
                            const isSectionEditing = editingSection?.sessionKey === sessionKey && editingSection?.section === 'requirements';
                            return (
                              <li key={req.id || idx} style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                                {isEditing ? (
                                  <div style={{ flex: 1, display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                                    <textarea
                                      defaultValue={req.requirement_text}
                                      onBlur={(e) => {
                                        const newText = e.target.value;
                                        if (newText.trim() !== req.requirement_text) {
                                          handleSaveRequirement(sessionKey, idx, newText);
                                        } else {
                                          setEditingRequirement(null);
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && e.ctrlKey) {
                                          e.target.blur();
                                        }
                                        if (e.key === 'Escape') {
                                          setEditingRequirement(null);
                                        }
                                      }}
                                      style={{
                                        flex: 1,
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        borderRadius: '6px',
                                        padding: '0.5rem',
                                        color: '#ffffff',
                                        fontSize: '0.875rem',
                                        fontFamily: 'inherit',
                                        resize: 'vertical',
                                        minHeight: '60px'
                                      }}
                                      autoFocus
                                    />
                                    {gameStatus === 'draft' && (
                                      <button
                                        onClick={() => handleDeleteRequirement(sessionKey, idx)}
                                        style={{
                                          padding: '0.25rem',
                                          background: 'transparent',
                                          border: 'none',
                                          color: '#FF3B30',
                                          fontSize: '1rem',
                                          cursor: 'pointer',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          width: '24px',
                                          height: '24px'
                                        }}
                                        title="Delete requirement"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#FF3B30"><path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z"/></svg>
                                      </button>
                                    )}
                      </div>
                                ) : (
                                  <>
                                    <span>{req.requirement_text}</span>
                                    {gameStatus === 'draft' && isSectionEditing && (
                                      <>
                                        <button
                                          onClick={() => setEditingRequirement({ sessionKey, index: idx })}
                                          style={{
                                            padding: '0.25rem',
                                            background: 'transparent',
                                            border: 'none',
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            fontSize: '1rem',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '24px',
                                            height: '24px',
                                            marginLeft: '0.5rem'
                                          }}
                                          title="Edit requirement"
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="rgba(255, 255, 255, 0.7)"><path d="M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Zm640-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z"/></svg>
                                        </button>
                                        <button
                                          onClick={() => handleDeleteRequirement(sessionKey, idx)}
                                          style={{
                                            padding: '0.25rem',
                                            background: 'transparent',
                                            border: 'none',
                                            color: '#FF3B30',
                                            fontSize: '1rem',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '24px',
                                            height: '24px',
                                            marginLeft: '0.25rem'
                                          }}
                                          title="Delete requirement"
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#FF3B30"><path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z"/></svg>
                                        </button>
                                      </>
                                    )}
                                  </>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <p style={{ color: 'rgba(255, 255, 255, 0.5)', marginTop: '0.5rem', fontStyle: 'italic' }}>
                          No requirements added yet
                        </p>
                      )}
                    </div>

                      <div style={{ marginBottom: '1rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <strong style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Client Changes ({changes.length}):</strong>
                          {gameStatus === 'draft' && (
                            <button
                              onClick={() => setEditingSection(editingSection?.sessionKey === sessionKey && editingSection?.section === 'clientChanges' ? null : { sessionKey, section: 'clientChanges' })}
                              style={{
                                padding: '0.25rem',
                                background: 'transparent',
                                border: 'none',
                                color: 'rgba(255, 255, 255, 0.7)',
                                fontSize: '1rem',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '24px',
                                height: '24px'
                              }}
                              title="Edit client changes"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="rgba(255, 255, 255, 0.7)"><path d="M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Zm640-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z"/></svg>
                            </button>
                          )}
                        </div>
                        {gameStatus === 'draft' && editingSection?.sessionKey === sessionKey && editingSection?.section === 'clientChanges' && (
                          <button
                            onClick={() => {
                              // Use prompt to get client change text
                              const text = window.prompt('Enter client change text:');
                              if (text && text.trim()) {
                                // Use default values for points and timing
                                handleAddClientChange(sessionKey, text.trim(), 1);
                              }
                            }}
                            style={{
                              padding: '0.25rem 0.75rem',
                              background: 'rgba(215, 2, 246, 0.2)',
                              border: '1px solid #D702F6',
                              borderRadius: '6px',
                              color: '#D702F6',
                              fontSize: '0.875rem',
                              cursor: 'pointer'
                            }}
                          >
                            + Add
                          </button>
                        )}
                      </div>
                      
                      {changes.length > 0 ? (
                        <ul style={{ color: 'rgba(255, 255, 255, 0.7)', marginTop: '0.5rem', paddingLeft: 0, lineHeight: '1.6', listStyle: 'none' }}>
                          {changes.map((change, idx) => {
                            const isEditing = editingClientChange?.sessionKey === sessionKey && editingClientChange?.index === idx;
                            const isSectionEditing = editingSection?.sessionKey === sessionKey && editingSection?.section === 'clientChanges';
                            return (
                              <li key={change.id || idx} style={{ marginBottom: '0.75rem', padding: '0.75rem', background: 'rgba(255, 255, 255, 0.03)', borderRadius: '6px', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                                {isEditing ? (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                    <textarea
                                      value={editingClientChangeValues?.text ?? change.text}
                                      placeholder="Client change text..."
                                      onChange={(e) => {
                                        setEditingClientChangeValues({
                                          ...(editingClientChangeValues || { text: change.text, points: change.points || 1 }),
                                          text: e.target.value
                                        });
                                      }}
                                      style={{
                                        width: '100%',
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        border: '1px solid rgba(255, 255, 255, 0.2)',
                                        borderRadius: '6px',
                                        padding: '0.5rem',
                                        color: '#ffffff',
                                        fontSize: '0.875rem',
                                        fontFamily: 'inherit',
                                        resize: 'vertical',
                                        minHeight: '60px'
                                      }}
                                      autoFocus
                                    />
                                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                      <label style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem' }}>
                                        Points:
                                        <input
                                          type="number"
                                          value={editingClientChangeValues?.points ?? (change.points || 1)}
                                          min="1"
                                          onChange={(e) => {
                                            setEditingClientChangeValues({
                                              ...(editingClientChangeValues || { text: change.text, points: change.points || 1 }),
                                              points: parseInt(e.target.value) || 1
                                            });
                                          }}
                                          style={{
                                            marginLeft: '0.5rem',
                                            width: '60px',
                                            background: 'rgba(255, 255, 255, 0.1)',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            borderRadius: '4px',
                                            padding: '0.25rem',
                                            color: '#ffffff',
                                            fontSize: '0.875rem'
                                          }}
                                        />
                                      </label>
                                      <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
                                        <button
                                          onClick={() => {
                                            const values = editingClientChangeValues || { text: change.text, points: change.points || 1 };
                                            handleSaveClientChange(sessionKey, idx, values);
                                            setEditingClientChangeValues(null);
                                          }}
                                          disabled={saving}
                                          style={{
                                            padding: '0.375rem 0.75rem',
                                            background: '#D702F6',
                                            border: '1px solid #D702F6',
                                            borderRadius: '6px',
                                            color: '#ffffff',
                                            fontSize: '0.875rem',
                                            cursor: saving ? 'not-allowed' : 'pointer',
                                            opacity: saving ? 0.6 : 1,
                                            fontWeight: '500'
                                          }}
                                        >
                                          {saving ? 'Saving...' : 'Save'}
                                        </button>
                                        <button
                                          onClick={() => {
                                            setEditingClientChange(null);
                                            setEditingClientChangeValues(null);
                                          }}
                                          disabled={saving}
                                          style={{
                                            padding: '0.375rem 0.75rem',
                                            background: 'transparent',
                                            border: '1px solid rgba(255, 255, 255, 0.3)',
                                            borderRadius: '6px',
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            fontSize: '0.875rem',
                                            cursor: saving ? 'not-allowed' : 'pointer',
                                            opacity: saving ? 0.6 : 1
                                          }}
                                        >
                                          Cancel
                                        </button>
                                        {gameStatus === 'draft' && (
                                          <button
                                            onClick={() => handleDeleteClientChange(sessionKey, idx)}
                                            disabled={saving}
                                            style={{
                                              padding: '0.25rem',
                                              background: 'transparent',
                                              border: 'none',
                                              color: '#FF3B30',
                                              fontSize: '1rem',
                                              cursor: saving ? 'not-allowed' : 'pointer',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              width: '24px',
                                              height: '24px',
                                              opacity: saving ? 0.6 : 1
                                            }}
                                            title="Delete client change"
                                          >
                                            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#FF3B30"><path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z"/></svg>
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.5rem' }}>
                                    <div style={{ flex: 1 }}>
                                      <div>{change.text}</div>
                                      <div style={{ marginTop: '0.25rem', fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.5)' }}>
                                        <span style={{ color: '#D702F6' }}>{change.points || 1} pts</span>
                                      </div>
                                    </div>
                                    {gameStatus === 'draft' && isSectionEditing && (
                                      <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                                        <button
                                          onClick={() => {
                                            setEditingClientChange({ sessionKey, index: idx });
                                            setEditingClientChangeValues({
                                              text: change.text,
                                              points: change.points || 1
                                            });
                                          }}
                                          style={{
                                            padding: '0.25rem',
                                            background: 'transparent',
                                            border: 'none',
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            fontSize: '1rem',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '24px',
                                            height: '24px'
                                          }}
                                          title="Edit client change"
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="rgba(255, 255, 255, 0.7)"><path d="M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Zm640-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z"/></svg>
                                        </button>
                                        <button
                                          onClick={() => handleDeleteClientChange(sessionKey, idx)}
                                          style={{
                                            padding: '0.25rem',
                                            background: 'transparent',
                                            border: 'none',
                                            color: '#FF3B30',
                                            fontSize: '1rem',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '24px',
                                            height: '24px'
                                          }}
                                          title="Delete client change"
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#FF3B30"><path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Zm400-600H280v520h400v-520ZM360-280h80v-360h-80v360Zm160 0h80v-360h-80v360ZM280-720v520-520Z"/></svg>
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                            </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <p style={{ color: 'rgba(255, 255, 255, 0.5)', marginTop: '0.5rem', fontStyle: 'italic' }}>
                          No client changes added yet
                        </p>
                    )}
                    </div>

                      <div style={{
                        marginTop: '1rem',
                        padding: '1rem',
                      background: bonus && bonus.text ? 'rgba(215, 2, 246, 0.1)' : 'rgba(255, 255, 255, 0.03)',
                        borderLeft: '3px solid #D702F6',
                        borderRadius: '6px'
                      }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: bonus && bonus.text ? '0.5rem' : '0' }}>
                        <strong style={{ color: '#D702F6' }}>Bonus Client Change:</strong>
                        {gameStatus === 'draft' && bonus && bonus.text && !editingBonus && (
                          <button
                            onClick={() => {
                              setEditingBonus(sessionKey);
                              setEditingBonusValues({
                                text: bonus.text,
                                points: bonus.points || 4
                              });
                            }}
                            style={{
                              padding: '0.25rem',
                              background: 'transparent',
                              border: 'none',
                              color: 'rgba(255, 255, 255, 0.7)',
                              fontSize: '1rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '24px',
                              height: '24px'
                            }}
                            title="Edit bonus change"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="rgba(255, 255, 255, 0.7)"><path d="M200-200h57l391-391-57-57-391 391v57Zm-80 80v-170l528-527q12-11 26.5-17t30.5-6q16 0 31 6t26 18l55 56q12 11 17.5 26t5.5 30q0 16-5.5 30.5T817-647L290-120H120Zm640-584-56-56 56 56Zm-141 85-28-29 57 57-29-28Z"/></svg>
                          </button>
                        )}
                      </div>
                      
                      {editingBonus === sessionKey ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          <textarea
                            value={editingBonusValues?.text ?? (bonus?.text || '')}
                            placeholder="Enter bonus client change text..."
                            onChange={(e) => {
                              setEditingBonusValues({
                                ...(editingBonusValues || { text: bonus?.text || '', points: bonus?.points || 4 }),
                                text: e.target.value
                              });
                            }}
                            style={{
                              width: '100%',
                              background: 'rgba(255, 255, 255, 0.1)',
                              border: '1px solid rgba(255, 255, 255, 0.2)',
                              borderRadius: '6px',
                              padding: '0.5rem',
                              color: '#ffffff',
                              fontSize: '0.875rem',
                              fontFamily: 'inherit',
                              resize: 'vertical',
                              minHeight: '60px'
                            }}
                            autoFocus
                          />
                          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <label style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem' }}>
                              Points:
                              <input
                                type="number"
                                value={editingBonusValues?.points ?? (bonus?.points || 4)}
                                min="1"
                                onChange={(e) => {
                                  setEditingBonusValues({
                                    ...(editingBonusValues || { text: bonus?.text || '', points: bonus?.points || 4 }),
                                    points: parseInt(e.target.value) || 4
                                  });
                                }}
                                style={{
                                  marginLeft: '0.5rem',
                                  width: '60px',
                                  background: 'rgba(255, 255, 255, 0.1)',
                                  border: '1px solid rgba(255, 255, 255, 0.2)',
                                  borderRadius: '4px',
                                  padding: '0.25rem',
                                  color: '#ffffff',
                                  fontSize: '0.875rem'
                                }}
                              />
                            </label>
                            <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto' }}>
                              <button
                                onClick={() => {
                                  const values = editingBonusValues || { text: bonus?.text || '', points: bonus?.points || 4 };
                                  handleSaveBonus(sessionKey, values);
                                  setEditingBonusValues(null);
                                }}
                                disabled={saving}
                                style={{
                                  padding: '0.375rem 0.75rem',
                                  background: '#D702F6',
                                  border: '1px solid #D702F6',
                                  borderRadius: '6px',
                                  color: '#ffffff',
                                  fontSize: '0.875rem',
                                  cursor: saving ? 'not-allowed' : 'pointer',
                                  opacity: saving ? 0.6 : 1,
                                  fontWeight: '500'
                                }}
                              >
                                {saving ? 'Saving...' : 'Save'}
                              </button>
                              <button
                                onClick={() => {
                                  setEditingBonus(null);
                                  setEditingBonusValues(null);
                                }}
                                disabled={saving}
                                style={{
                                  padding: '0.375rem 0.75rem',
                                  background: 'transparent',
                                  border: '1px solid rgba(255, 255, 255, 0.3)',
                                  borderRadius: '6px',
                                  color: 'rgba(255, 255, 255, 0.7)',
                                  fontSize: '0.875rem',
                                  cursor: saving ? 'not-allowed' : 'pointer',
                                  opacity: saving ? 0.6 : 1
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : bonus && bonus.text ? (
                        <>
                        <p style={{ color: 'rgba(255, 255, 255, 0.9)', marginTop: '0.5rem', marginBottom: '0.25rem', whiteSpace: 'pre-wrap' }}>
                          {bonus.text}
                        </p>
                          <div style={{ fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.5)' }}>
                            <span style={{ color: '#D702F6' }}>{bonus.points || 4} points</span>
                      </div>
                        </>
                      ) : (
                        gameStatus === 'draft' && (
                          <div style={{ marginTop: '0.5rem' }}>
                            <textarea
                              placeholder="Enter bonus client change text..."
                              onBlur={(e) => {
                                if (e.target.value.trim()) {
                                  handleSaveBonus(sessionKey, {
                                    text: e.target.value.trim(),
                                    points: 4
                                  });
                                }
                              }}
                              style={{
                                width: '100%',
                                background: 'rgba(255, 255, 255, 0.05)',
                                border: '1px dashed rgba(255, 255, 255, 0.2)',
                                borderRadius: '6px',
                                padding: '0.5rem',
                                color: '#ffffff',
                                fontSize: '0.875rem',
                                fontFamily: 'inherit',
                                resize: 'vertical',
                                minHeight: '60px',
                                marginBottom: '0.5rem'
                              }}
                            />
                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                              <label style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.875rem' }}>
                                Points:
                                <input
                                  type="number"
                                  defaultValue={4}
                                  min="1"
                                  onBlur={(e) => {
                                    const textarea = e.target.parentElement.parentElement.querySelector('textarea');
                                    if (textarea && textarea.value.trim()) {
                                      handleSaveBonus(sessionKey, {
                                        text: textarea.value.trim(),
                                        points: parseInt(e.target.value) || 4
                                      });
                                    }
                                  }}
                                  style={{
                                    marginLeft: '0.5rem',
                                    width: '60px',
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    borderRadius: '4px',
                                    padding: '0.25rem',
                                    color: '#ffffff',
                                    fontSize: '0.875rem'
                                  }}
                                />
                              </label>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                );
              }))}
            </div>

            {/* Resources Section */}
            <div style={{ marginBottom: '2rem' }}>
              <ResourceSection
                resources={resources}
                setResources={setResources}
                onResourcesChange={handleResourcesChange}
                currentGameId={game?.id}
              />
            </div>
          </div>
        </div>
      </main>
    </>
  );
}

export default GameManagement;

